import { useEffect, useId, useMemo, useRef, useState } from "react";

import {
  type GroupedTimezones,
  groupTimezonesByRegion,
} from "../../routes/policies/schedules/utils/groupTimezonesByRegion";
import { t } from "../../lib/i18n";

const FALLBACK_ZONES = [
  "UTC",
  "Europe/Istanbul",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Paris",
  "America/New_York",
  "America/Los_Angeles",
  "America/Chicago",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Australia/Sydney",
];

function getAllTimezones(): string[] {
  try {
    const intl = Intl as unknown as {
      supportedValuesOf?: (key: string) => string[];
    };
    if (typeof intl.supportedValuesOf === "function") {
      return intl.supportedValuesOf("timeZone");
    }
  } catch {
    /* fall through */
  }
  return FALLBACK_ZONES;
}

const MAX_RESULTS = 80;

interface TimezoneAutocompleteProps {
  value: string;
  onChange: (next: string) => void;
  id?: string;
  placeholder?: string;
}

export function TimezoneAutocomplete({
  value,
  onChange,
  id,
  placeholder = "Europe/Istanbul",
}: TimezoneAutocompleteProps) {
  const generatedId = useId();
  const inputId = id ?? `tz-${generatedId}`;
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => setQuery(value), [value]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const grouped = useMemo<GroupedTimezones[]>(
    () => groupTimezonesByRegion(getAllTimezones()),
    [],
  );

  const filteredGroups = useMemo<GroupedTimezones[]>(() => {
    const q = query.trim().toLowerCase();
    let remaining = MAX_RESULTS;
    const result: GroupedTimezones[] = [];
    for (const group of grouped) {
      if (remaining <= 0) break;
      const zones = q
        ? group.zones.filter((z) => z.toLowerCase().includes(q))
        : group.zones;
      if (zones.length === 0) continue;
      const slice = zones.slice(0, remaining);
      result.push({ region: group.region, zones: slice });
      remaining -= slice.length;
    }
    return result;
  }, [query, grouped]);

  function commit(tz: string) {
    onChange(tz);
    setQuery(tz);
    setOpen(false);
  }

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <input
        id={inputId}
        type="text"
        className="input"
        value={query}
        placeholder={placeholder}
        onChange={(e) => {
          setQuery(e.target.value);
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        autoComplete="off"
        aria-label={t("schedules.editor.field.timezone")}
      />
      {open && filteredGroups.length > 0 && (
        <div
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 40,
            background: "var(--color-surface)",
            border: "1px solid var(--color-border-strong)",
            borderRadius: 6,
            maxHeight: 320,
            overflow: "auto",
            boxShadow: "var(--shadow-lg)",
            padding: 4,
          }}
        >
          {filteredGroups.map((group) => (
            <div key={group.region} role="group" aria-label={group.region}>
              <div
                style={{
                  padding: "8px 10px 4px",
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  color: "var(--color-text-muted)",
                }}
              >
                {t(`schedules.timezone.regions.${group.region.toLowerCase()}`, group.region)}
              </div>
              {group.zones.map((tz) => (
                <button
                  key={tz}
                  type="button"
                  role="option"
                  aria-selected={tz === value}
                  onClick={() => commit(tz)}
                  style={{
                    display: "block",
                    width: "100%",
                    padding: "6px 10px",
                    border: 0,
                    borderRadius: 4,
                    background: tz === value ? "var(--color-accent-soft)" : "transparent",
                    textAlign: "left",
                    cursor: "pointer",
                    font: "inherit",
                    fontSize: 13,
                    fontFamily: "var(--font-mono)",
                  }}
                >
                  {tz}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
